// A player's "Take back move" (issue 1436) in the browser: the player's ask, and the DM's requests waiting with Allow and
// Refuse, over `table:takeback` (server/src/takeback.ts). The requests come from the server alone, to the DM's sockets
// alone: a player's page never holds anyone's request, its own included; it is told the answer as a notice.
import { useSyncExternalStore } from 'react';
import type { Socket } from 'socket.io-client';

export const TABLE_TAKEBACK = 'table:takeback';
export interface TakebackAsk { player: string; handle: string; scene: string; summary: string; rolls: number; at: string }
type Reply = { ok: true; asks?: TakebackAsk[]; away?: boolean } | { ok: false; error: string; reason?: string };

let asks: TakebackAsk[] = [];
let socket: Socket | null = null;
let campaignId = '';
const listeners = new Set<() => void>();
const set = (next: TakebackAsk[]) => { asks = next; for (const fn of listeners) fn(); };
const onAsks = (raw: unknown) => {
  const m = raw as { campaignId?: unknown; asks?: unknown } | null;
  if (m?.campaignId === campaignId && Array.isArray(m.asks)) set(m.asks as TakebackAsk[]);
};

function request(body: Record<string, unknown>): Promise<Reply> {
  const s = socket;
  if (!s || !s.connected) return Promise.resolve({ ok: false, error: 'refused', reason: 'You are disconnected; nothing you do now is sent.' });
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, error: 'refused', reason: 'That did not reach the table in time. Try again.' }), 10_000);
    s.emit(TABLE_TAKEBACK, { campaignId, ...body }, (r: Reply) => { clearTimeout(timer); resolve(r ?? { ok: false, error: 'refused' }); });
  });
}

export const takebacks = {
  get: () => asks,
  subscribe(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; },
  /** The table this page sits at: the DM's waiting requests read now (anyone else is refused, and holds none). */
  attach(s: Socket, id: string, dm: boolean) {
    if (socket !== s || campaignId !== id) { socket?.off(TABLE_TAKEBACK, onAsks); set([]); }
    socket = s; campaignId = id;
    s.off(TABLE_TAKEBACK, onAsks); s.on(TABLE_TAKEBACK, onAsks);
    if (dm) void request({ op: 'list' }).then((r) => { if (r.ok && r.asks) set(r.asks); });
    else set([]);
  },
  /** A player's ask: the words to show, whatever came of it. */
  async ask(): Promise<string> {
    const r = await request({ op: 'ask' });
    if (!r.ok) return r.reason ?? 'That could not be asked just now.';
    return r.away ? 'The DM is away. Your request to take back your move waits for them.' : 'Asked the DM to take back your move. Nothing changes unless they allow it.';
  },
  answer: (player: string, allow: boolean) => request({ op: 'answer', player, allow }),
};

export const useTakebacks = (): TakebackAsk[] => useSyncExternalStore(takebacks.subscribe, takebacks.get, takebacks.get);

/** The DM's requests waiting, one notice each with Allow and Refuse; says when the move set off rolls (they stand). */
export function TakebackNotices({ nameOf, onRefused }: { nameOf: (id: string, handle: string) => string; onRefused: (reason: string) => void }) {
  const waiting = useTakebacks();
  if (!waiting.length) return null;
  const tap = 'rounded border px-2 py-0.5';
  return (
    <>
      {waiting.map((a) => (
        <div key={a.player} role="alert" data-testid={`takeback-${a.player}`} className="pointer-events-auto flex flex-wrap items-center gap-2 rounded bg-stone-900/95 px-3 py-1 text-xs text-stone-200">
          <span>{`${nameOf(a.player, a.handle)} asks to take back a move: ${a.summary}.`}{a.rolls ? ` It set off ${a.rolls === 1 ? 'a roll' : `${a.rolls} rolls`}, which ${a.rolls === 1 ? 'stands' : 'stand'}.` : ''}</span>
          <button type="button" className={`${tap} border-emerald-600`} data-testid="takeback-allow"
            onClick={() => { void takebacks.answer(a.player, true).then((r) => { if (!r.ok) onRefused(r.reason ?? 'That could not be undone just now.'); }); }}>Allow</button>
          <button type="button" className={`${tap} border-stone-600`} data-testid="takeback-refuse"
            onClick={() => { void takebacks.answer(a.player, false); }}>Refuse</button>
        </div>
      ))}
    </>
  );
}
